import { createApiClient } from '@/core/api';
import { useCallback, useMemo } from 'react';
import {
  tenantKycToStripeQueryParam,
  type CreateTenantRequest,
  type TenantsListResponse,
  type TenantFilters,
  type Tenant,
  type PatchTenantRequest,
  type TenantDeleteEligibility,
} from '../models';
import { TENANT_ENDPOINTS } from '../constants';
import {
  normalizeTenantsListBody,
  unwrapTenantResponseBody,
  unwrapDeleteEligibilityBody,
} from '../utils/tenantApi.mapper';

/** Query string for GET /api/portal-admin/tenants (pagination/search/KYC only). */
export function buildTenantListQueryParams(
  filters?: TenantFilters
): Record<string, string | number | boolean> {
  if (!filters) return {};
  const params: Record<string, string | number | boolean> = {};
  if (typeof filters.page === 'number' && filters.page >= 1) {
    params.page = filters.page;
  }
  if (typeof filters.limit === 'number' && filters.limit >= 1) {
    params.limit = filters.limit;
  }
  const q = filters.search?.trim();
  if (q) params.search = q;
  const stripe = tenantKycToStripeQueryParam(filters.kycStatus);
  if (stripe) params.stripeKycStatus = stripe;
  return params;
}

export const useTenantService = () => {
  const apiClient = useMemo(
    () => createApiClient({ isMock: false }),
    []
  );

  const getTenants = useCallback(
    async (filters?: TenantFilters): Promise<TenantsListResponse> => {
      const params: Record<string, string | number | boolean> = {
        ...buildTenantListQueryParams(filters),
      };
      if (filters?.includeInactive) {
        params.includeInactive = true;
      }
      const response = await apiClient.get<unknown>(TENANT_ENDPOINTS.TENANTS, {
        params,
      });
      return normalizeTenantsListBody(response.data);
    },
    [apiClient]
  );

  const createTenant = useCallback(
    async (payload: CreateTenantRequest): Promise<Tenant> => {
      const response = await apiClient.post<unknown>(
        TENANT_ENDPOINTS.CREATE,
        payload
      );
      return unwrapTenantResponseBody(response.data);
    },
    [apiClient]
  );

  const getTenantById = useCallback(
    async (tenantId: string): Promise<Tenant> => {
      const response = await apiClient.get<unknown>(
        TENANT_ENDPOINTS.BY_ID(tenantId)
      );
      return unwrapTenantResponseBody(response.data);
    },
    [apiClient]
  );

  const updateTenant = useCallback(
    async (
      tenantId: string,
      payload: PatchTenantRequest
    ): Promise<Tenant> => {
      const response = await apiClient.patch<unknown>(
        TENANT_ENDPOINTS.BY_ID(tenantId),
        payload
      );
      return unwrapTenantResponseBody(response.data);
    },
    [apiClient]
  );

  const deleteTenant = useCallback(
    async (tenantId: string): Promise<void> => {
      await apiClient.delete(TENANT_ENDPOINTS.BY_ID(tenantId));
    },
    [apiClient]
  );

  const getDeleteEligibility = useCallback(
    async (tenantId: string): Promise<TenantDeleteEligibility> => {
      const response = await apiClient.get<unknown>(
        TENANT_ENDPOINTS.DELETE_ELIGIBILITY(tenantId)
      );
      return unwrapDeleteEligibilityBody(response.data);
    },
    [apiClient]
  );

  return {
    getTenants,
    createTenant,
    getTenantById,
    updateTenant,
    deleteTenant,
    getDeleteEligibility,
  };
};
